import {
  IconButton,
  ITextProps,
  Label,
  Modal,
  Text,
  TextField,
} from "@fluentui/react";
import { AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import { ApiService } from "../../../services/api.service";
import { HelperService } from "../../../services/helper.service";
import {
  cancelIcon,
  contentStyles,
  iconButtonStyles,
  labelStyles,
  valueStyles,
} from "../../Controls/component-styles";
import {
  contextSegments,
  FormMode,
  InputType,
} from "../../Controls/context-segments";
import DeleteDialog from "../../Controls/Dialogs/DeleteDialog";
import Dropdown from "../../Controls/Dropdown/Dropdown";
import { Option } from "../../Controls/Dropdown/option";
import { DynamicFormCommandBar } from "../../Controls/DynamicFormCommandBar/DynamicFormCommandBar";
import { IGoodCategoryDocument } from "../GoodCategory/good-category.type";
import GoodCategoryForm from "../GoodCategory/GoodCategoryForm";
import { IGoodUnitDocument } from "../GoodUnit/good-unit.type";
import GoodUnitForm from "../GoodUnit/GoodUnitForm";
import { IGoodDTO, transferDTO } from "./good.dto";
import { attributes, Good, IGoodDocument } from "./good.type";

interface IProps {
  isModalOpen: boolean;
  selectedItem: IGoodDocument | null;
  formMode: FormMode;
  name?: string;
  closeModal: () => void;
  setFormMode: (formMode: FormMode) => void;
  setReload?: (reload: boolean) => void;
  setCreatedItem?: (e: any, newItem: any) => void;
}
const helperService = HelperService.getInstance();

const apiCalls: ApiService = ApiService.getInstance();
const formName: string = "good-form";

export const GoodForm = (props: IProps) => {
  // Own state
  const [payload, setPayload] = useState<IGoodDocument>(new Good());
  const [hideDeleteDialog, toggleHideDeleteDialog] = useState<boolean>(true);

  //   GoodUnit
  const [isGoodUnitModalOpen, setIsGoodUnitModalOpen] =
    useState<boolean>(false);
  const [goodUnitFormMode, setGoodUnitFormMode] = useState<FormMode>(
    FormMode.create
  );
  const [selectedGoodUnitItem, setSelectedGoodUnitItem] =
    useState<IGoodUnitDocument | null>(null);
  const [shouldSetDefaultGoodUnitOption, setShouldSetDefaultGoodUnitOption] =
    useState<boolean>(true);

  const closeGoodUnitModal = async () => {
    setIsGoodUnitModalOpen(false);
    setSelectedGoodUnitItem(null);
    setGoodUnitFormMode(FormMode.create);
  };

  const setGoodUnitOption = async (e: any, newValue: any) => {
    handleOnChange(e, newValue);
    // Signal that the item need to be preselected in the dropdown
    setShouldSetDefaultGoodUnitOption(true);
  };

  //   GoodCategory
  const [isGoodCategoryModalOpen, setIsGoodCategoryModalOpen] =
    useState<boolean>(false);
  const [goodCategoryFormMode, setGoodCategoryFormMode] = useState<FormMode>(
    FormMode.create
  );
  const [selectedGoodCategoryItem, setSelectedGoodCategoryItem] =
    useState<IGoodCategoryDocument | null>(null);
  const [
    shouldSetDefaultGoodCategoryOption,
    setShouldSetDefaultGoodCategoryOption,
  ] = useState<boolean>(true);

  const closeGoodCategoryModal = async () => {
    setIsGoodCategoryModalOpen(false);
    setSelectedGoodCategoryItem(null);
    setGoodCategoryFormMode(FormMode.create);
  };

  const setGoodCategoryOption = async (e: any, newValue: any) => {
    handleOnChange(e, newValue);
    // Signal that the item need to be preselected in the dropdown
    setShouldSetDefaultGoodCategoryOption(true);
  };
  const formHeading: string = helperService.getFormHeading(
    contextSegments.good.singularName,
    props.formMode
  );

  useEffect(() => {
    // In case some item was passed to the form
    // Set this item to the payload
    if (props.selectedItem) {
      setPayload(props.selectedItem);
    }
    // In case no item was passed to the form
    // Set default empty payload
    else {
      setPayload(new Good());
      setShouldSetDefaultGoodUnitOption(true);
      setShouldSetDefaultGoodCategoryOption(true);
    }
    // Clear the input when closing the modal
    if (!props.isModalOpen) {
      setPayload(new Good());
    }
  }, [props.selectedItem, props.isModalOpen]);

  const titleId = helperService.makeId(10);

  /**
   * Invoke create action
   * @param e
   */
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payloadDto: IGoodDTO = transferDTO(payload);
    // In case payload id exist
    // Update the item
    if (payload.id) {
      apiCalls
        .updateGood(payloadDto, payload.id)
        .then((res: AxiosResponse<any>) => {
          props.setReload && props.setReload(true);

          props.closeModal();
          setPayload(new Good());
        });
    }
    // In case no payload id exist
    // Create a new item
    else {
      apiCalls.createGood(payloadDto).then((res: AxiosResponse<any>) => {
        //   In case creating an item through the dropdown context
        if (props.setCreatedItem && props.name && res.data.id) {
          const fakeEvent = {
            target: {
              name: props.name,
              type: "dropdown",
            },
          };
          const newItem: IGoodDocument = {
            id: res.data["id"],
            titleDe: res.data["titleDe"],
            titleRu: res.data["titleRu"],
            price: res.data["price"],
            weight: res.data["weight"],
            tara: res.data["tara"],
            code: res.data["code"],
            goodUnit: res.data["goodUnit"],
            goodCategory: res.data["goodCategory"],
          };

          props.setCreatedItem(fakeEvent, newItem);
        }
        props.setReload && props.setReload(true);

        props.closeModal();
        setPayload(new Good());
      });
    }
  };

  /**
   * Write newValue to state by element name
   * @param e
   * @param newValue
   */
  const handleOnChange = (e: any, newValue: any) => {
    const { name } = e.target;

    if (newValue === undefined) {
      setPayload({ ...payload, [name]: null });
    } else {
      const inputType: string = e.target.type;
      if (inputType === InputType.text || inputType === InputType.textarea) {
        setPayload({ ...payload, [name]: String(newValue) });
      } else if (inputType === InputType.number) {
        setPayload({ ...payload, [name]: Number(newValue) });
      } else if (inputType === InputType.checkbox) {
        setPayload({ ...payload, [name]: Boolean(newValue) });
      } else if (inputType === InputType.dropdown) {
        setPayload({ ...payload, [name]: newValue });
      }
    }
  };
  const isEditable: boolean =
    props.formMode === FormMode.create || props.formMode === FormMode.edit;
  const deleteOpenedItem = async () => {
    toggleHideDeleteDialog(true);
    props.closeModal();
    await apiCalls.deleteGood(props.selectedItem!.id!);
    props.setReload && props.setReload(true);
  };

  return (
    <Modal
      titleAriaId={titleId}
      isOpen={props.isModalOpen}
      onDismiss={props.closeModal}
      isBlocking={false}
      containerClassName={contentStyles.container}
    >
      <DeleteDialog
        toggleHideDeleteDialog={toggleHideDeleteDialog}
        hideDeleteDialog={hideDeleteDialog}
        selectedItemsCount={1}
        deleteSelectedItems={deleteOpenedItem}
      />
      <div className={contentStyles.header}>
        <Text id={titleId} variant={"xxLarge" as ITextProps["variant"]}>
          {formHeading}
        </Text>
        <IconButton
          styles={iconButtonStyles}
          iconProps={cancelIcon}
          ariaLabel="Close popup modal"
          onClick={props.closeModal}
        />
      </div>
      <div className={contentStyles.body}>
        {isEditable && props.isModalOpen ? (
          <form id={formName} onSubmit={onSubmit}>
            <DynamicFormCommandBar
              formMode={props.formMode}
              toggleHideDeleteDialog={toggleHideDeleteDialog}
              closeModal={props.closeModal}
              formName={formName}
            />
            <Dropdown
              shouldSetDefaultOption={shouldSetDefaultGoodUnitOption}
              isMulti={false}
              contextKey={contextSegments.goodUnit.contextKey}
              optionKey={contextSegments.goodUnit.displayAttribute}
              attributeName={attributes.goodUnit.name}
              label={attributes.goodUnit.label}
              setOption={setGoodUnitOption}
              setShouldSetDefaultOption={setShouldSetDefaultGoodUnitOption}
              showModal={() => setIsGoodUnitModalOpen(true)}
              defaultOption={
                new Option(
                  String(payload.goodUnit?.title),
                  String(payload.goodUnit?.id)
                )
              }
            />
            <Dropdown
              shouldSetDefaultOption={shouldSetDefaultGoodCategoryOption}
              isMulti={false}
              contextKey={contextSegments.goodCategory.contextKey}
              optionKey={contextSegments.goodCategory.displayAttribute}
              attributeName={attributes.goodCategory.name}
              label={attributes.goodCategory.label}
              setOption={setGoodCategoryOption}
              setShouldSetDefaultOption={setShouldSetDefaultGoodCategoryOption}
              showModal={() => setIsGoodCategoryModalOpen(true)}
              defaultOption={
                new Option(
                  String(payload.goodCategory?.title),
                  String(payload.goodCategory?.id)
                )
              }
            />
            <TextField
              name={attributes.titleDe.name}
              label={attributes.titleDe.label}
              type="text"
              required
              onChange={handleOnChange}
              defaultValue={payload.titleDe}
            />
            <TextField
              name={attributes.titleRu.name}
              label={attributes.titleRu.label}
              type="text"
              required
              onChange={handleOnChange}
              defaultValue={payload.titleRu}
            />

            <TextField
              name={attributes.price.name}
              label={attributes.price.label}
              suffix="€"
              type="number"
              onChange={handleOnChange}
              defaultValue={String(payload.price)}
            />
            <TextField
              name={attributes.weight.name}
              label={attributes.weight.label}
              suffix="Kg"
              type="number"
              onChange={handleOnChange}
              defaultValue={String(payload.weight)}
            />
            <TextField
              name={attributes.tara.name}
              label={attributes.tara.label}
              suffix="Kg"
              type="number"
              onChange={handleOnChange}
              defaultValue={String(payload.tara)}
            />
            <TextField
              name={attributes.code.name}
              label={attributes.code.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={String(payload.code || "")}
            />
          </form>
        ) : (
          <div>
            <DynamicFormCommandBar
              formMode={props.formMode}
              toggleHideDeleteDialog={toggleHideDeleteDialog}
              formName={formName}
              setFormMode={props.setFormMode}
              closeModal={props.closeModal}
            />
            <Label styles={labelStyles}>{attributes.goodUnit.label}</Label>
            <span style={valueStyles}>{payload.goodUnit?.title}</span>
            <Label styles={labelStyles}>{attributes.goodCategory.label}</Label>
            <span style={valueStyles}>{payload.goodCategory?.title}</span>
            <Label styles={labelStyles}>{attributes.titleDe.label}</Label>
            <span style={valueStyles}>{payload.titleDe}</span>
            <Label styles={labelStyles}>{attributes.titleRu.label}</Label>
            <span style={valueStyles}>{payload.titleRu}</span>
            <Label styles={labelStyles}>{attributes.price.label}</Label>
            <span style={valueStyles}>{payload.price} €</span>
            <Label styles={labelStyles}>{attributes.weight.label}</Label>
            <span style={valueStyles}>{payload.weight} Kg</span>
            <Label styles={labelStyles}>{attributes.tara.label}</Label>
            <span style={valueStyles}>{payload.tara} Kg</span>
            <Label styles={labelStyles}>{attributes.code.label}</Label>
            <span style={valueStyles}>{payload.code}</span>
          </div>
        )}
      </div>
      {/* Additional Forms */}
      <GoodUnitForm
        isModalOpen={isGoodUnitModalOpen}
        formMode={goodUnitFormMode}
        selectedItem={selectedGoodUnitItem}
        fkName={attributes.goodUnit.name}
        setFormMode={setGoodUnitFormMode}
        closeModal={closeGoodUnitModal}
        setCreatedItem={setGoodUnitOption}
      />
      <GoodCategoryForm
        isModalOpen={isGoodCategoryModalOpen}
        formMode={goodCategoryFormMode}
        selectedItem={selectedGoodCategoryItem}
        fkName={attributes.goodCategory.name}
        setFormMode={setGoodCategoryFormMode}
        closeModal={closeGoodCategoryModal}
        setCreatedItem={setGoodCategoryOption}
      />
    </Modal>
  );
};
